import { useCallback, useEffect, useRef } from 'react'
import { useReducedMotion } from 'framer-motion'
import { haptic } from '@/lib/haptics'

interface UseDragDismissOptions {
  /** 닫기 콜백 (드래그 임계 초과 시 호출) */
  onClose: () => void
  /** 시트 높이 대비 닫힘 임계 비율 (기본 30%) */
  dismissRatio?: number
  /** 닫힘으로 판정할 최소 속도 (px/s, 기본 600) */
  velocityThreshold?: number
  /** 비활성화 (예: 데스크톱 중앙 모달일 때) */
  disabled?: boolean
}

/**
 * 바텀 시트 드래그-투-디스미스 훅.
 *
 * 그랩 핸들(또는 시트 헤더)에 `dragHandlers`를 바인딩하면 손가락을 따라 시트가
 * 아래로 끌려 내려가며(위로는 저항), 놓는 순간 deltaY가 시트 높이의
 * `dismissRatio`를 넘거나 속도가 빠르면 `onClose`를 호출한다. 그렇지 않으면
 * 스프링으로 제자리에 돌아온다.
 *
 * 구현 메모: 패널 `transform`을 ref로 직접 조작한다(인라인). 이렇게 하면
 * Headless UI의 `transition` 기반 진입/이탈 애니메이션(`data-[closed]` CSS)을
 * 가로채지 않고, 드래그 중에만 일시적으로 transform/transition을 덮어쓴 뒤
 * 정착/닫힘 후 원래 상태로 복구한다. (framer `motion.create`는 `transition`
 * prop을 흡수해 Headless 트랜지션을 깨므로 사용하지 않는다.)
 *
 * - 시트에 `sheetRef`를 달아 transform 대상 + 높이 측정에 사용한다.
 * - 스크롤 영역 내부에서 시작된 드래그는 잡지 않으므로 핸들/헤더에만 바인딩한다.
 * - prefers-reduced-motion 시 드래그-팔로우를 끄고 탭-투-클로즈 경로만 유지한다.
 */
export function useDragDismiss({
  onClose,
  dismissRatio = 0.3,
  velocityThreshold = 600,
  disabled = false,
}: UseDragDismissOptions) {
  const shouldReduceMotion = useReducedMotion()
  const sheetRef = useRef<HTMLDivElement | null>(null)

  // 드래그 추적 상태 (리렌더 유발하지 않도록 ref로 관리)
  const startY = useRef(0)
  const lastY = useRef(0)
  const lastTime = useRef(0)
  const velocity = useRef(0)
  const dragging = useRef(false)
  const pointerId = useRef<number | null>(null)
  const rafId = useRef<number | null>(null)

  const dragEnabled = !disabled && !shouldReduceMotion

  // 인라인 transform/transition을 비워 Headless의 진입/이탈 애니메이션으로 되돌린다.
  const clearTransform = useCallback(() => {
    const el = sheetRef.current
    if (!el) return
    el.style.transform = ''
    el.style.transition = ''
  }, [])

  // 언마운트 시 RAF 정리
  useEffect(() => {
    return () => {
      if (rafId.current != null) cancelAnimationFrame(rafId.current)
    }
  }, [])

  const applyOffset = useCallback((offset: number) => {
    const el = sheetRef.current
    if (!el) return
    // 위로 끌면 러버밴드 저항, 아래로는 1:1
    const next = offset < 0 ? offset * 0.25 : offset
    el.style.transition = 'none'
    el.style.transform = `translateY(${next}px)`
  }, [])

  const springBack = useCallback(() => {
    const el = sheetRef.current
    if (!el) return
    // 짧은 ease-out으로 제자리 복귀 후 인라인 스타일 제거
    el.style.transition = 'transform 0.28s cubic-bezier(0.22, 1, 0.36, 1)'
    el.style.transform = 'translateY(0px)'
    let done = false
    const onEnd = () => {
      if (done) return
      done = true
      el.removeEventListener('transitionend', onEnd)
      clearTransform()
    }
    el.addEventListener('transitionend', onEnd)
    // transition이 발생하지 않는 경우(이동량 0) 대비 안전망
    window.setTimeout(onEnd, 320)
  }, [clearTransform])

  const handlePointerDown = useCallback(
    (e: React.PointerEvent) => {
      if (!dragEnabled) return
      if (e.button != null && e.button !== 0) return
      dragging.current = true
      pointerId.current = e.pointerId
      startY.current = e.clientY
      lastY.current = e.clientY
      lastTime.current = e.timeStamp
      velocity.current = 0
      try {
        ;(e.currentTarget as HTMLElement).setPointerCapture(e.pointerId)
      } catch {
        /* 일부 브라우저는 캡처 거부 — 무시 */
      }
    },
    [dragEnabled]
  )

  const handlePointerMove = useCallback(
    (e: React.PointerEvent) => {
      if (!dragging.current || pointerId.current !== e.pointerId) return
      const offset = e.clientY - startY.current

      const dt = e.timeStamp - lastTime.current
      if (dt > 0) {
        velocity.current = ((e.clientY - lastY.current) / dt) * 1000
      }
      lastY.current = e.clientY
      lastTime.current = e.timeStamp

      if (rafId.current != null) cancelAnimationFrame(rafId.current)
      rafId.current = requestAnimationFrame(() => applyOffset(offset))
    },
    [applyOffset]
  )

  const finishDrag = useCallback(
    (e: React.PointerEvent) => {
      if (!dragging.current || pointerId.current !== e.pointerId) return
      dragging.current = false
      pointerId.current = null
      if (rafId.current != null) {
        cancelAnimationFrame(rafId.current)
        rafId.current = null
      }
      try {
        ;(e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId)
      } catch {
        /* 무시 */
      }

      const offset = e.clientY - startY.current
      const sheetHeight = sheetRef.current?.offsetHeight ?? 0
      const distanceThreshold = sheetHeight > 0 ? sheetHeight * dismissRatio : 120
      const shouldDismiss =
        offset > distanceThreshold || velocity.current > velocityThreshold

      if (shouldDismiss) {
        haptic('light')
        const el = sheetRef.current
        if (el && sheetHeight > 0) {
          // 화면 밖으로 마저 내린 뒤 닫기. inline transform(=sheetHeight)은
          // 닫힘 상태(translate-y-full 등)와 시각적으로 일치하므로 onClose 직전에
          // 지우지 않아 스냅을 방지한다. 언마운트로 잔여 스타일은 무해.
          el.style.transition = 'transform 0.18s ease-in'
          el.style.transform = `translateY(${sheetHeight}px)`
          let closed = false
          const finish = () => {
            if (closed) return
            closed = true
            el.removeEventListener('transitionend', finish)
            onClose()
          }
          el.addEventListener('transitionend', finish)
          // transitionend 누락 대비 안전망
          window.setTimeout(finish, 260)
        } else {
          clearTransform()
          onClose()
        }
      } else {
        springBack()
      }
    },
    [dismissRatio, velocityThreshold, onClose, springBack, clearTransform]
  )

  // 비활성 상태에서는 핸들러를 비워 네이티브 스크롤/탭 동작을 방해하지 않는다.
  const dragHandlers = dragEnabled
    ? {
        onPointerDown: handlePointerDown,
        onPointerMove: handlePointerMove,
        onPointerUp: finishDrag,
        onPointerCancel: finishDrag,
        style: { touchAction: 'none' as const, cursor: 'grab' as const },
      }
    : {}

  return {
    /** 시트 컨테이너 ref (transform 대상 + 높이 측정) */
    sheetRef,
    /** 그랩 핸들/헤더에 스프레드할 포인터 핸들러 (+touchAction) */
    dragHandlers,
    /** 드래그 활성 여부 */
    dragEnabled,
  }
}
